import { useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EstimatedTag } from './ui/EstimatedTag';
import type { CostBreakdownResource } from '@lizrd/contracts';
import { getCostBreakdown, getGraphNode } from '../lib/api';
import { categoryColor, PROVIDER_ACCENT, typeLabel, usd } from '../lib/graph-visuals';
import { CostCategoryIcon, NodeTypeIcon } from './node-icons';
import { ResourceDetailModal } from './ResourceDetailModal';

// --- Squarified treemap layout (Bruls et al.) — pure, no dependencies. -------
type Rect = { x: number; y: number; w: number; h: number };
type Item<T> = { value: number; data: T };
type Laid<T> = Rect & { data: T };

function squarify<T>(items: Item<T>[], rect: Rect): Laid<T>[] {
  const out: Laid<T>[] = [];
  const clean = items.filter((i) => i.value > 0).sort((a, b) => b.value - a.value);
  const total = clean.reduce((s, i) => s + i.value, 0);
  if (total <= 0 || rect.w <= 0 || rect.h <= 0) return out;
  const scale = (rect.w * rect.h) / total;
  const areas = clean.map((i) => ({ a: i.value * scale, data: i.data }));

  const worst = (row: number[], side: number) => {
    const sum = row.reduce((s, v) => s + v, 0);
    const s2 = sum * sum;
    const side2 = side * side;
    return Math.max((side2 * Math.max(...row)) / s2, s2 / (side2 * Math.min(...row)));
  };

  let { x, y, w, h } = rect;
  let idx = 0;
  while (idx < areas.length) {
    const side = Math.min(w, h);
    const row: { a: number; data: T }[] = [];
    while (idx < areas.length) {
      const cur = row.map((r) => r.a);
      if (row.length === 0 || worst([...cur, areas[idx]!.a], side) <= worst(cur, side))
        row.push(areas[idx++]!);
      else break;
    }
    const rowSum = row.reduce((s, r) => s + r.a, 0);
    const thick = rowSum / side;
    let pos = 0;
    if (w >= h) {
      for (const r of row) {
        const ch = r.a / thick;
        out.push({ x, y: y + pos, w: thick, h: ch, data: r.data });
        pos += ch;
      }
      x += thick;
      w -= thick;
    } else {
      for (const r of row) {
        const cw = r.a / thick;
        out.push({ x: x + pos, y, w: cw, h: thick, data: r.data });
        pos += cw;
      }
      y += thick;
      h -= thick;
    }
  }
  return out;
}

const HEIGHT = 440;
const HEADER = 22; // category label strip
const GAP = 3;
const PROVIDER_ORDER = ['AWS', 'GCP', 'AZURE'];
const providerLabel = (p: string) => (p === 'AZURE' ? 'Azure' : p);

/** A merged category for the current provider filter. */
interface TreemapCategory {
  category: string;
  total: number;
  resourceCount: number;
  topResources: CostBreakdownResource[];
}

/** A resource tile, or an "Other (N)" bucket rolling up a category's long tail
 *  (the resources past the server's top-N — their detail is not itemized here). */
type Cell =
  | { kind: 'resource'; r: CostBreakdownResource }
  | { kind: 'other'; cat: string; count: number; cost: number };

/**
 * Cost treemap: category regions (labelled header) subdivide into resource tiles
 * sized by monthly cost — the biggest tile is the biggest spend. Fed by the
 * SERVER-SIDE cost aggregate (`getCostBreakdown`) computed over every node, so the
 * biggest tiles are the real biggest spenders (not the alphabetical first page the old
 * capped-graph treemap could show). Each category's resources past the aggregate's
 * top-N roll into a clickable-free "Other (N)" bucket. Tiles open the resource detail
 * (fetched by id); unattributed cost is flagged amber.
 */
export function CostTreemap({ envId }: { envId: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [provider, setProvider] = useState('All');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]!.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const breakdown = useQuery({
    queryKey: ['cost-breakdown', envId],
    queryFn: () => getCostBreakdown(envId),
  });
  // Lazily fetch the full node only when a tile is opened — the aggregate carries the
  // tile's display fields, but the detail modal needs the node's full properties.
  const detail = useQuery({
    queryKey: ['graph-node', envId, selectedId],
    queryFn: () => getGraphNode(envId, selectedId!),
    enabled: selectedId != null,
  });

  const data = breakdown.data;
  if (!data) return null;

  const allBilled = data.provenance === 'billed';
  const providerNames = data.providers.map((p) => p.provider);
  const filters = ['All', ...providerNames].sort(
    (a, b) =>
      (a === 'All' ? -1 : PROVIDER_ORDER.indexOf(a) + 1 || 99) -
        (b === 'All' ? -1 : PROVIDER_ORDER.indexOf(b) + 1 || 99) || a.localeCompare(b),
  );

  // Build the categories for the current filter. "All" merges same-named categories
  // across providers (sum totals + counts, concat the per-provider top-N and re-sort).
  const catMap = new Map<string, TreemapCategory>();
  for (const p of data.providers) {
    if (provider !== 'All' && p.provider !== provider) continue;
    for (const c of p.categories) {
      const m =
        catMap.get(c.category) ??
        catMap
          .set(c.category, { category: c.category, total: 0, resourceCount: 0, topResources: [] })
          .get(c.category)!;
      m.total += c.total;
      m.resourceCount += c.resourceCount;
      m.topResources.push(...c.topResources);
    }
  }
  const cats = [...catMap.values()];
  for (const c of cats) c.topResources.sort((a, b) => b.monthlyCost - a.monthlyCost);
  const grand = cats.reduce((s, c) => s + c.total, 0);

  const catRects =
    width > 0 && grand > 0
      ? squarify(
          cats.map((c) => ({ value: c.total, data: c })),
          { x: 0, y: 0, w: width, h: HEIGHT },
        )
      : [];

  return (
    <div className="space-y-2">
      {/* Provider filter */}
      <div className="flex flex-wrap items-center gap-1">
        {filters.map((p) => {
          const active = provider === p;
          const accent = p === 'All' ? 'var(--iris)' : (PROVIDER_ACCENT[p] ?? '#8b93a7');
          return (
            <button
              key={p}
              onClick={() => setProvider(p)}
              className="flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[0.7rem] font-semibold transition-colors"
              style={
                active
                  ? { backgroundColor: accent, color: '#04140d', borderColor: accent }
                  : { color: 'var(--haze)', borderColor: 'var(--hairline)' }
              }
            >
              {p !== 'All' && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: active ? '#04140d' : accent }}
                />
              )}
              {p === 'All' ? 'All clouds' : providerLabel(p)}
            </button>
          );
        })}
        {!allBilled && <EstimatedTag className="ml-auto" />}
      </div>

      <div ref={ref} className="relative w-full" style={{ height: HEIGHT }}>
        {catRects.map((cr) => {
          const cat = cr.data;
          const color = categoryColor(cat.category);
          const inner: Rect = {
            x: cr.x + GAP,
            y: cr.y + HEADER,
            w: Math.max(0, cr.w - GAP * 2),
            h: Math.max(0, cr.h - HEADER - GAP),
          };

          // Big tiles = the aggregate's top-N; the remainder (count + cost) is one
          // "Other" cell so a long tail never turns the region into a mosaic.
          const shownCost = cat.topResources.reduce((s, r) => s + r.monthlyCost, 0);
          const tailCount = cat.resourceCount - cat.topResources.length;
          const tailCost = Math.max(0, cat.total - shownCost);
          const cells: Cell[] = [
            ...cat.topResources.map((r): Cell => ({ kind: 'resource', r })),
            ...(tailCount > 0 && tailCost > 0
              ? [{ kind: 'other', cat: cat.category, count: tailCount, cost: tailCost } as Cell]
              : []),
          ];

          const tiles = squarify(
            cells.map((c) => ({
              value: c.kind === 'resource' ? c.r.monthlyCost : c.cost,
              data: c,
            })),
            inner,
          );

          return (
            <div key={cat.category}>
              <div
                className="absolute overflow-hidden rounded-md"
                style={{
                  left: cr.x + 1,
                  top: cr.y + 1,
                  width: Math.max(0, cr.w - 2),
                  height: Math.max(0, cr.h - 2),
                  backgroundColor: `${color}14`,
                  border: `1px solid ${color}55`,
                }}
              >
                <div
                  className="truncate px-2 py-1 text-[0.7rem] font-bold"
                  style={{ color }}
                  title={`${cat.category} · ${usd(cat.total)}/mo`}
                >
                  <CostCategoryIcon
                    category={cat.category}
                    className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom text-muted"
                  />{' '}
                  {cat.category} · {usd(cat.total)}
                </div>
              </div>

              {tiles.map(({ x, y, w, h, data }) => {
                const tileBox = {
                  left: x + 1,
                  top: y + 1,
                  width: Math.max(0, w - 2),
                  height: Math.max(0, h - 2),
                };
                const showLabel = w > 74 && h > 34;

                if (data.kind === 'other') {
                  return (
                    <div
                      key={`other:${cat.category}`}
                      title={`${data.count.toLocaleString()} smaller resources · ${usd(data.cost)}/mo (beyond the top ${cat.topResources.length} by cost)`}
                      className="absolute overflow-hidden rounded-sm"
                      style={{
                        ...tileBox,
                        backgroundColor: `${color}66`,
                        border: `1px dashed ${color}`,
                      }}
                    >
                      {showLabel && (
                        <div className="p-1.5 leading-tight text-bone">
                          <div className="truncate text-[0.65rem] font-semibold">
                            ⋯ Other ({data.count.toLocaleString()})
                          </div>
                          <div className="text-[0.6rem] text-haze">{usd(data.cost)}/mo</div>
                        </div>
                      )}
                    </div>
                  );
                }

                const r = data.r;
                const orphan = !r.owned;
                const accent = PROVIDER_ACCENT[r.provider] ?? '#8b93a7';
                return (
                  <button
                    key={r.nodeId}
                    onClick={() => setSelectedId(r.nodeId)}
                    className="absolute overflow-hidden rounded-sm text-left transition-[filter] hover:brightness-125"
                    style={{
                      ...tileBox,
                      backgroundColor: `${color}cc`,
                      border: orphan ? '1.5px dashed var(--warning)' : `1px solid ${color}`,
                    }}
                    title={`${r.name} · ${typeLabel(r.type, r.provider)} · ${providerLabel(r.provider)} · ${usd(r.monthlyCost)}/mo${orphan ? ' · unattributed' : ''} — click for details`}
                  >
                    {showLabel && (
                      <div className="p-1.5 leading-tight text-on-iris">
                        <div className="truncate pr-8 text-[0.65rem] font-semibold">
                          {orphan && '⚠ '}
                          <NodeTypeIcon
                            type={r.type}
                            className="mr-1 inline-block h-3.5 w-3.5 align-text-bottom text-muted"
                          />{' '}
                          {r.name}
                        </div>
                        <div className="text-[0.6rem] opacity-80">{usd(r.monthlyCost)}/mo</div>
                      </div>
                    )}
                    {provider === 'All' && showLabel && (
                      <span
                        className="absolute right-1 top-1 rounded px-1 py-0.5 text-[0.5rem] font-bold"
                        style={{ backgroundColor: 'rgba(0,0,0,0.4)', color: accent }}
                      >
                        {providerLabel(r.provider)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <ResourceDetailModal
        node={selectedId && detail.data ? detail.data : null}
        envId={envId}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
